import { type Dispatch, type SetStateAction, useState } from 'react'
import { type CompanyId, type SmallUser, type TeamId, type RoleId, isNotDefined, userHasRole } from '@terros/common'
import { useDefaultTeam } from '../team/defaultTeam'
import { type GetHookResponse } from '../hooks'
import { matchUser, sortUsersByFirstName, sortUsersByLastName } from '../data'
import { useUsers } from './list'
import { useProfile } from './get'

type Props = {
  companyId?: CompanyId
  show?: 'company' | 'downline'
  showBeta?: boolean
  teamId?: TeamId
  roleId?: RoleId
  showArchived?: boolean
  filter?: string
}

export type UserListSortType = 'lastName' | 'firstName' | 'lastAccess'

export type UserListState = GetHookResponse<SmallUser[]> &
  Props & {
    filtered: SmallUser[]
    showArchived: boolean
    setShowArchived: (value: boolean) => void
    sortBy: UserListSortType
    setSortBy: (value: UserListSortType) => void
    sortInverted: boolean
    setSortInverted: Dispatch<SetStateAction<boolean>>
    managing: boolean
    setManaging: Dispatch<SetStateAction<boolean>>
    teamId?: TeamId
    setTeamId: Dispatch<SetStateAction<TeamId | undefined>>
    roleId?: RoleId
    setRoleId: Dispatch<SetStateAction<RoleId | undefined>>
    filter: string
    setFilter: (value: string) => void
  }

export function useUserListState({
  companyId,
  show = 'downline',
  showBeta,
  teamId: teamIdProp,
  roleId: roleIdProp,
  showArchived: showArchivedProp,
  filter: filterProp,
}: Props = {}): UserListState {
  const { isAdmin, isTantalim } = useProfile()
  const defaultTeam = useDefaultTeam({ companyId })

  const [filter, setFilter] = useState('')
  const [teamId, setTeamId] = useState<TeamId | undefined>(show === 'downline' ? defaultTeam : undefined)
  const [roleId, setRoleId] = useState<RoleId | undefined>(undefined)
  const [sortBy, setSortBy] = useState<UserListSortType>('lastName')
  const [managing, setManaging] = useState<boolean>(!isAdmin && !isTantalim)
  const [sortInverted, setSortInverted] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const effectiveTeamId = teamIdProp ?? teamId
  const effectiveRoleId = roleIdProp ?? roleId
  const effectiveShowArchived = showArchivedProp ?? showArchived
  const effectiveFilter = filterProp ?? filter
  const userList = useUsers({ companyId, showArchived: effectiveShowArchived, teamId: effectiveTeamId, showBeta })
  const allUsers = userList.data ?? []
  const filteredUsers = allUsers.filter((row) => matchUser(row, effectiveFilter))
  const subList = filteredUsers.filter((row) => isNotDefined(effectiveRoleId) || userHasRole(row, effectiveRoleId))
  if (sortBy === 'lastAccess') subList.sort((a, b) => (a.lastAccess ?? Infinity) - (b.lastAccess ?? Infinity))
  if (sortBy === 'lastName') subList.sort(sortUsersByLastName)
  if (sortBy === 'firstName') subList.sort(sortUsersByFirstName)
  if (sortInverted) subList.reverse()

  return {
    ...userList,
    data: allUsers,
    filtered: subList,
    showArchived: effectiveShowArchived,
    setShowArchived,
    sortBy,
    setSortBy,
    sortInverted,
    setSortInverted,
    filter: effectiveFilter,
    setFilter,
    managing,
    setManaging,
    teamId: effectiveTeamId,
    setTeamId,
    roleId: effectiveRoleId,
    setRoleId,
    companyId,
  }
}
